import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";

import classNames from "classnames";
import { MotionConfig } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import {
  BuildingState,
  setCurrentFloorID,
} from "@/features/cartography/cartographySlice";
import { selectConfigItem, selectEmbed } from "../config/configSlice";
import { selectTrajectory } from "../map/mapSlice";
import { selectProfileName } from "../session/sessionSlice";
import {
  AppUI,
  EditUI,
  selectAuthReady,
  selectEditMode,
  selectError,
  selectFeedbackModalOpen,
  selectInitialWindowSize,
  selectLoading,
  selectMapLoaded,
  selectOnline,
  selectUIMode,
  setAuthReady,
  setError,
  setLoading,
  setUIMode,
  type WindowDimensions,
} from "./appSlice";

import useKeyboardListener from "@/app/hooks/useKeyboardListener";
import useRefresh from "@/app/hooks/useRefresh";
import { useScreenReadersFocus } from "@/app/hooks/useScreenReadersFocus";
import useServiceWorker from "@/app/hooks/useServiceWorker";
import {
  getWindowSize,
  updateInitialWindowSize,
} from "@/app/hooks/useWindowDimensions";
import useTheme from "@/features/app/hooks/theme/useTheme";
import useLanguage from "@/features/app/hooks/useLanguage";
import useOnlineDetector from "@/features/app/hooks/useOnlineDetector";
import useURLParamsUpdater from "@/features/app/hooks/useURLParamsUpdater";
import {
  selectBuildings,
  selectCurrentBuilding,
  selectCurrentBuildingID,
  selectCurrentBuildingState,
} from "@/features/cartography/cartographySlice.selectors";
import { loadConfig } from "@/features/config/actions";
import { useWebviewApiListener, WebviewApi } from "@/features/webview_api";
import {
  useAppDispatch,
  useAppSelector,
  useWindowDimensions,
} from "../../app/hooks";
import usePrefetch from "../../app/hooks/usePrefetch";
import type { Floor } from "../../libs/models";
import useActions from "../map/hooks/useActions";
import { useNativeRoutes } from "../native_routes/NativeRoutesProvider";
import useNavigation from "../navigation/hooks/useNavigation";
import {
  selectNavigationFrom,
  selectNavigationTo,
} from "../navigation/navigationSlice.selectors";
import { useSitumAPIContext } from "../situm_api/SitumAPIProvider";
import { LoadingFrame } from "./components/loading/LoadingFrame";
import useSpeechSynthesis from "./hooks/useSpeechSynthesis";
import useURLIdentifierParams from "./hooks/useURLIdentifierParams";
import type { SearchParamsSitum } from "./types";

import "react-tooltip/dist/react-tooltip.css";
import "unfonts.css";
import "./styles/index.scss";

import { useFlightInformationPolling } from "@/features/flight_information/useFlightInformationPolling";
import useLocationShare from "../share_location/useLocationShare";

// Components
const MapWrapper = lazy(() => import("../map/MapWrapper"));
const MainPanel = lazy(() => import("../app/components/panels/MainPanel"));
const KSidebar = lazy(
  () => import("@/features/kiosk_mode/components/k_sidebar/KSidebar"),
);
const KSelectedEntityDetail = lazy(
  () =>
    import(
      "@/features/kiosk_mode/components/k_selected_detail/KSelectedEntityDetail"
    ),
);
const MainHandler = lazy(() => import("../app/MainHandler"));
const LocationSelector = lazy(
  () => import("../location_explorer/LocationExplorerPanel"),
);
const ShareQRPanel = lazy(() => import("../share_map/ShareMapPanel"));
const Editor = lazy(() => import("@/features/editor/components/editor/Editor"));
const FeedbackModal = lazy(() => import("../feedback/FeedbackModal"));
const LocationDebugPanel = lazy(() => import("../location/LocationDebugPanel"));
const TrajectoryControl = lazy(() => import("../location/TrajectoryControl"));
const CalibrationPanel = lazy(async () => {
  return {
    default: (await import("../calibration/components/CalibrationPanel"))
      .CalibrationPanel,
  };
});
const LongRouteWarning = lazy(
  () => import("./long_route_warning/LongRouteWarning"),
);
const FpsTracker = lazy(() => import("../profile/FpsTracker"));
const ErrorHandler = lazy(() => import("@/features/error_screen/ErrorHandler"));
const TopBanners = lazy(() => import("@/features/top_banners/TopBanner"));

const ShareLocationModal = lazy(
  () => import("../share_location/modals/ShareLocationModal"),
);

/// <reference types="vite-plugin-svgr/client" />
function App() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const dispatch = useAppDispatch();

  const [configReady, setConfigReady] = useState(false);

  const profileUrl =
    searchParams.get("config_file") || params.config_file || null; // TODO deprecate
  const profileDomain =
    searchParams.get("config_domain" satisfies SearchParamsSitum) || null;
  const floorid = useAppSelector(selectConfigItem("map.initialView.floor_id"));
  const { t } = useTranslation();
  const error = useAppSelector(selectError);
  const loading = useAppSelector(selectLoading);
  const allowCrawlerIndex = useAppSelector(
    selectConfigItem("internal.crawlersAllowIndex"),
  );
  const buildings = useAppSelector(selectBuildings);
  const currentBuilding = useAppSelector(selectCurrentBuilding);
  const currentBuildingState = useAppSelector(selectCurrentBuildingState);
  const currentBuildingID = useAppSelector(selectCurrentBuildingID);
  const navigationFrom = useAppSelector(selectNavigationFrom);
  const navigationTo = useAppSelector(selectNavigationTo);
  const initialWindowSize = useAppSelector(selectInitialWindowSize);
  const currentUI = useAppSelector(selectUIMode);
  const currentEditMode = useAppSelector(selectEditMode);
  const isOnline = useAppSelector(selectOnline);
  const authReady = useAppSelector(selectAuthReady);
  const trajectory = useAppSelector(selectTrajectory);

  const debug = useAppSelector(selectConfigItem("internal.debug"));
  const profile = useAppSelector(selectConfigItem("internal.profile"));
  const profileName = useAppSelector(selectProfileName);
  const kioskMode = useAppSelector(
    selectConfigItem("feature_flags.kiosk_mode"),
  );
  const showExplorerPanel = useAppSelector(
    selectConfigItem("ui.widget_visibility.exploration_control"),
  );
  const showSearchBar = useAppSelector(
    selectConfigItem("ui.widget_visibility.search_bar"),
  );
  const showDirectionsPanel = useAppSelector(
    selectConfigItem("ui.widget_visibility.directions_panel"),
  );
  const isEmbed = useAppSelector(selectEmbed);
  const appName = useAppSelector(selectConfigItem("internal.appName"));
  const longRouteWarning = useAppSelector(
    selectConfigItem("directions.longRouteWarning"),
  );
  const isFeedbackModalOpen = useAppSelector(selectFeedbackModalOpen);
  const shareLocationModal = useAppSelector(
    selectConfigItem("feature_flags.share_location"),
  );
  const [windowSize] = useState<WindowDimensions>(getWindowSize());
  const [windowSizeLoaded, setWindowSizeLoaded] = useState(false);

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [mapIsReadySent, setMapIsReadySent] = useState(false);
  const reducedMotion = useAppSelector(selectConfigItem("ui.reducedMotion"));
  const isLocationPickerMode = currentUI === AppUI.locationPicker;
  const isOnEditMode =
    currentEditMode != null && currentEditMode !== EditUI.settings;
  const shouldShowKioskExplorerPanel =
    kioskMode &&
    showExplorerPanel &&
    showSearchBar &&
    !isOnEditMode &&
    !isLocationPickerMode &&
    currentUI === AppUI.explore;
  const shouldShowKioskDirectionsPanel =
    kioskMode &&
    showDirectionsPanel &&
    !isOnEditMode &&
    !isLocationPickerMode &&
    currentUI === AppUI.directions;
  const shouldShowKioskSidebar =
    shouldShowKioskExplorerPanel || shouldShowKioskDirectionsPanel;

  useKeyboardListener();
  useWebviewApiListener(dispatch);
  useTheme();
  useOnlineDetector();
  useLanguage();
  useWindowDimensions();
  usePrefetch();
  useURLParamsUpdater();
  useURLIdentifierParams();
  useRefresh();
  useSpeechSynthesis();
  useNavigation();
  useScreenReadersFocus();
  useLocationShare();
  useFlightInformationPolling();
  const { setForceNetworkFirst } = useServiceWorker();
  const { routesCalculator } = useNativeRoutes();
  const { modals } = useActions();

  const { initApp, loadBuilding } = useSitumAPIContext();

  const isMapLoaded = useAppSelector(selectMapLoaded);
  const jwt = useAppSelector(selectConfigItem("auth.jwt"));

  // Load config on start

  // Check if the app is waiting for authentication using the message app.set_auth
  useEffect(() => {
    if (
      searchParams.get("wait_for_auth" satisfies SearchParamsSitum) === "true"
    ) {
      WebviewApi.onAuthIsReady();
      return;
    }

    dispatch(setAuthReady(true));
  }, []);

  // Load config when auth is ready
  useEffect(() => {
    if (!authReady) return;

    loadConfig(searchParams, profileUrl ?? "", profileDomain)
      .then(() => {
        setConfigReady(true);
      })
      .catch((e) => {
        console.error(e);
        dispatch(setError(e));
      });
  }, [authReady]);

  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    if (pathSegments[0] === "id" && pathSegments[1]) {
      const newPath = `/${pathSegments[1]}${location.search}${location.hash}`;
      window.history.replaceState(null, "", newPath);
    }
  }, [location]);

  // Set up initial window size
  useLayoutEffect(() => {
    const dimensions = getWindowSize();

    updateInitialWindowSize(dimensions, initialWindowSize);
  }, []);

  useEffect(() => {
    updateInitialWindowSize(windowSize, initialWindowSize);
  }, [windowSize]);

  // Start the app when config is loaded
  useEffect(() => {
    if (!configReady) return;

    initApp(profileName).finally(() => {
      setInitialDataLoaded(true);
    });
  }, [configReady, jwt, profileName]);

  useEffect(() => {
    if (
      !loading &&
      !mapIsReadySent &&
      (currentBuildingState == BuildingState.COMPLETE ||
        currentBuildingState == BuildingState.PREFETCHED) &&
      routesCalculator != null
    ) {
      setMapIsReadySent(true);
      try {
        WebviewApi.onMapReady();
      } catch (error) {
        console.error("Error sending 'Map is Ready' message: ", error);
      }
    }
  }, [loading, currentBuildingState, routesCalculator]);

  useEffect(() => {
    if (allowCrawlerIndex) {
      document.querySelector("[name='robots']")?.remove();
      return;
    }

    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex");
    document.getElementsByTagName("head")[0].appendChild(meta);
  }, [allowCrawlerIndex]);

  useEffect(() => {
    if (initialWindowSize.height === 0 || initialWindowSize.width === 0) {
      return;
    }

    setWindowSizeLoaded(true);
  }, [initialWindowSize]);

  useEffect(() => {
    document.title = appName;
  }, [appName]);

  useEffect(() => {
    if (
      !configReady ||
      !initialDataLoaded ||
      !windowSizeLoaded ||
      !loading ||
      !isMapLoaded
    ) {
      return;
    }

    dispatch(setLoading(false));
  }, [configReady, initialDataLoaded, error, windowSizeLoaded, isMapLoaded]);

  // When current building changes, select current floor
  useEffect(() => {
    if (!buildings || buildings.length == 0 || !currentBuilding) {
      return;
    } // TODO: ERROR

    const floor =
      !floorid ||
      !currentBuilding?.floors?.map((f: Floor) => f.id).includes(floorid)
        ? currentBuilding?.getFirstFloor()
        : currentBuilding?.getFloorById(floorid) ||
          currentBuilding?.getFirstFloor();
    floor && dispatch(setCurrentFloorID(floor?.id));
  }, [currentBuildingID]);

  // When current building changes load data if necessary
  useEffect(() => {
    if (!currentBuilding || !isMapLoaded) {
      return;
    }

    // Only load building when it is not already loaded
    if (currentBuildingState === BuildingState.PARTIAL) {
      loadBuilding(currentBuilding);
    }
  }, [currentBuildingID, currentBuildingState, isOnline, isMapLoaded]);

  useEffect(() => {
    if (currentUI == AppUI.navigation) return;
    if (navigationTo || navigationFrom) {
      dispatch(setUIMode(AppUI.directions));
    }
  }, [navigationTo, navigationFrom]);

  useEffect(() => {
    if (!isEmbed) return;
    document.querySelector("html")?.setAttribute("data-is-embed", "true");
  }, [isEmbed]);

  useEffect(() => {
    setForceNetworkFirst(!isEmbed);
  }, [isEmbed]);

  useEffect(() => {
    const buttonAtribution = document.querySelector(
      'button[class="mapboxgl-ctrl-attrib-button"]',
    );
    buttonAtribution?.removeAttribute("tabindex");
    buttonAtribution?.setAttribute("tabindex", "-1");
    buttonAtribution?.removeAttribute("aria-label");
    buttonAtribution?.setAttribute("aria-hidden", "true");

    const maplibreControls = document.querySelector(
      'div[class="maplibregl-control-container"]',
    );
    maplibreControls?.setAttribute("aria-hidden", "true");

    const canvas = document.querySelectorAll("canvas");
    canvas.forEach((link) => {
      link.setAttribute("tabindex", "-1");
      link?.setAttribute("aria-hidden", "true");
      link.removeAttribute("aria-label");
      link.removeAttribute("role");
    });

    const links = document.querySelectorAll("a");
    links.forEach((link) => {
      link?.setAttribute("aria-hidden", "true");
      link.removeAttribute("aria-label");
      link.removeAttribute("tabindex");
      link.setAttribute("tabindex", "-1");
    });
  }, [currentBuilding]);

  return (
    <MotionConfig reducedMotion={reducedMotion}>
      <Suspense>
        {modals}
        {longRouteWarning.categoriesIds.length > 0 && <LongRouteWarning />}
        {isFeedbackModalOpen && <FeedbackModal />}
        {shareLocationModal && <ShareLocationModal />}
        <MainHandler />
        {authReady && !error && !loading && currentEditMode && <Editor />}
      </Suspense>

      <div data-body-scroll-lock-ignore="true" className="app-wrapper">
        <Suspense>
          {!authReady ? (
            <LoadingFrame
              message={t("app.waiting_for_authentication")}
              className="loading loading--fullscreen"
            />
          ) : !error && loading ? (
            <LoadingFrame
              message={t("app.loading_data")}
              className="loading loading--fullscreen"
            />
          ) : (
            <>
              <ErrorHandler />
              {!isLocationPickerMode && (
                <>
                  <TopBanners />
                  <MainPanel />
                  <LocationSelector />
                  <ShareQRPanel />
                  {currentUI == AppUI.calibration && <CalibrationPanel />}
                  {debug && <LocationDebugPanel />}
                  {trajectory?.data?.length > 0 && <TrajectoryControl />}
                  {profile && <FpsTracker />}
                </>
              )}
            </>
          )}
        </Suspense>
        {/* WARNING!!
          Do not remove this conditional rendering as it checks whether the size of the screen is not zero. It's essential in Flutter for iOS,
          especially in apps with tabs. In such cases, iOS initially loads the viewer with a (0, 0) window size, which later expands
          to the full screen. However, Mapbox occasionally fails to resize accurately, causing the map to appear partially grayed-out
          due to outdated values. To avoid this we only render the map when the screen size is greater than zero.
        */}
        {windowSizeLoaded && (
          <div
            className={classNames("app-wrapper__map-layout", {
              "app-wrapper__map-layout--kiosk": shouldShowKioskSidebar,
            })}
          >
            <Suspense>
              {shouldShowKioskSidebar && (
                <div className="k-wrapper">
                  <KSidebar />
                  <KSelectedEntityDetail />
                </div>
              )}
            </Suspense>
            <Suspense>
              <MapWrapper />
            </Suspense>
          </div>
        )}
      </div>
    </MotionConfig>
  );
}

export default App;
